import { Box, Tooltip } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme, WarningIcon } from '@tom-ui/ui'
import { MAX_INT32_VALUE } from '@tom-ui/utils'
import { OccupancyType } from '@yard/app/api'
import {
  blockedBackgroundPattern,
  resolveOccupancySchemeColor,
} from '@yard/utils/occupancies.utils'
import { observer } from 'mobx-react-lite'
import { ReactNode, useCallback, useMemo } from 'react'
import {
  CONTAINER_DETAILS_HEIGHT,
  ContainerViewMode,
  generateSlotKey,
} from '../../helpers/yard-view.utils'
import {
  isAccumulatorEligible,
  useAccumulatorClickHandler,
} from '../../hooks/use-accumulator-click-handler'
import { useDetailsGridRails } from '../../hooks/useDetailsGridRails'
import { HousekeepingPlanAccumulatorViewStore } from '../../stores/housekeeping-plan-accumulator.view-store'
import { YardViewStore } from '../../stores/yard.view-store'
import { BayGapCell } from '../atoms/BayGapCell'

interface Props {
  viewStore: YardViewStore
  housekeepingPlanAccumulatorViewStore: HousekeepingPlanAccumulatorViewStore
  renderContainerDetailsByJourneyId: (
    journeyId: number,
    highlight?: boolean,
    isReserved?: boolean,
    schemeBackgroundColor?: string,
  ) => ReactNode
}

export const YardBlockContainerDetailsGrid = observer(
  ({
    viewStore,
    housekeepingPlanAccumulatorViewStore,
    renderContainerDetailsByJourneyId,
  }: Props) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const handleAccumulatorClick = useAccumulatorClickHandler(housekeepingPlanAccumulatorViewStore)
    const isAccumulating = housekeepingPlanAccumulatorViewStore.isAccumulatorOpen

    const tierLength = viewStore.focusedYardBlock?.maxTiers ?? 1
    const columnLength = viewStore.containerDetailsColumns.length || 1

    const activeScheme = viewStore.colorScheme
    const selectedSchemeSet = useMemo(
      () => new Set(viewStore.effectiveSelectedValuesForActiveScheme),
      [viewStore.effectiveSelectedValuesForActiveScheme],
    )

    const {
      rails,
      totalCssColumns,
      gridTemplateColumns,
      hasAnyRail,
      cssColumnForDataIndex,
      extraGapColumnsInSpan,
    } = useDetailsGridRails(viewStore, columnLength)

    const getCellBackground = useCallback(
      (col: number, tier: number) => {
        let bay = viewStore.selectedBay
        let row = viewStore.selectedRow

        if (viewStore.containerViewMode === ContainerViewMode.BAY) {
          bay = viewStore.selectedBay!
          row = col
        } else if (viewStore.containerViewMode === ContainerViewMode.ROW) {
          bay = col
          row = viewStore.selectedRow!
        }

        const slotKey = generateSlotKey(bay!, row!)
        const config = viewStore.focusedYardBlock?.stackConfigArea?.[slotKey]
        if (!config) return undefined

        const hasMaxStackHeight =
          !!config.slotMaxStackHeight || !!config.rowMaxStackHeight || !!config.bayMaxStackHeight

        if (hasMaxStackHeight) {
          const maxStackHeight = Math.min(
            config.slotMaxStackHeight ?? MAX_INT32_VALUE,
            config.rowMaxStackHeight ?? MAX_INT32_VALUE,
            config.bayMaxStackHeight ?? MAX_INT32_VALUE,
          )

          if (tier > maxStackHeight)
            return `repeating-linear-gradient(
            135deg,
            ${theme.palette.grey[200]} 0px,
            ${theme.palette.grey[200]} 8px,
            ${theme.palette.grey[100]} 8px,
            ${theme.palette.grey[100]} 16px
          )`
        }

        if (config.isBlocked) return blockedBackgroundPattern('#f0f0f0')

        return undefined
      },
      [
        viewStore.containerViewMode,
        viewStore.focusedYardBlock,
        viewStore.selectedBay,
        viewStore.selectedRow,
        theme.palette.grey,
      ],
    )

    // Rail cells are intentionally rendered without icons — the icons are shown
    // in the footer (ghost variant) at a larger size per UX direction, keeping
    // the grid rails as clean visual separators only.
    const railCells = useMemo(() => {
      const result: ReactNode[] = []
      const tierSpan = `1 / span ${tierLength}`

      if (rails.leading) {
        result.push(
          <BayGapCell
            key='rail-leading'
            gap={rails.leading}
            variant='filled'
            sx={{ gridColumn: '1', gridRow: tierSpan }}
          />,
        )
      }

      if (rails.trailing) {
        result.push(
          <BayGapCell
            key='rail-trailing'
            gap={rails.trailing}
            variant='filled'
            sx={{ gridColumn: `${totalCssColumns}`, gridRow: tierSpan }}
          />,
        )
      }

      rails.inner.forEach((gap, dataIdx) => {
        const gapCol = cssColumnForDataIndex(dataIdx) + 1
        result.push(
          <BayGapCell
            key={`rail-gap-${dataIdx}`}
            gap={gap}
            variant='filled'
            sx={{ gridColumn: `${gapCol}`, gridRow: tierSpan }}
          />,
        )
      })

      return result
    }, [rails, tierLength, totalCssColumns, cssColumnForDataIndex])

    const dataCells = useMemo(() => {
      const result: ReactNode[] = []

      for (let tierIndex = 0; tierIndex < tierLength; tierIndex++) {
        const tier = viewStore.focusedYardBlock
          ? viewStore.focusedYardBlock.maxTiers - tierIndex
          : tierIndex + 1
        const tierData = viewStore.occupanciesForSelectedBayAndRow[tier] ?? {}
        const tierConflicts = viewStore.tierConflictsForSelectedBayAndRow[tier] ?? {}

        let colIndex = 0
        while (colIndex < columnLength) {
          const journey = tierData[colIndex]
          const journeyId = journey?.journeyId
          const hiddenContainers = tierConflicts[colIndex] ?? []

          let span = 1
          if (
            journeyId !== undefined &&
            colIndex + 1 < columnLength &&
            tierData[colIndex + 1]?.journeyId === journeyId
          ) {
            span = 2
          }

          const startDataIdx = colIndex + 1
          colIndex += span
          const background = getCellBackground(colIndex, tier)
          const startCssCol = cssColumnForDataIndex(startDataIdx)
          const gridColumnSpan = span + extraGapColumnsInSpan(startDataIdx, span)

          // Announced (Reserved) tiles are not clickable in accumulator mode —
          // the BE rejects them and we surface that as no-affordance + a
          // `not-allowed` cursor, not a dead click + warning. See AC #11 and
          // .spec-announced-container-filter.md.
          const isClickable = !!journeyId && isAccumulating && isAccumulatorEligible(journey)
          const isIneligibleInAccumulator =
            !!journeyId && isAccumulating && !isAccumulatorEligible(journey)
          // isAccumulated stays decoupled from isClickable so a Reserved sibling
          // tile of an already-accumulated container still shows the outline.
          const isAccumulated =
            !!journeyId &&
            isAccumulating &&
            housekeepingPlanAccumulatorViewStore.isAccumulated(journey.containerId)
          const cursor = isClickable
            ? 'pointer'
            : isIneligibleInAccumulator
              ? 'not-allowed'
              : undefined

          // Build the L2.3 race-drop snapshot from the cell's (block, bay/row, tier).
          // Bay/row depends on the current view mode — same translation
          // `getCellBackground` does. We're 1-indexed at startDataIdx.
          const cellBay =
            viewStore.containerViewMode === ContainerViewMode.ROW
              ? startDataIdx
              : viewStore.selectedBay
          const cellRow =
            viewStore.containerViewMode === ContainerViewMode.BAY
              ? startDataIdx
              : viewStore.selectedRow
          const blockId = viewStore.focusedYardBlock?.id
          const onCellClick =
            isClickable && blockId && cellBay != null && cellRow != null
              ? () =>
                  handleAccumulatorClick({
                    ...journey,
                    blockId,
                    position: { bay: cellBay, row: cellRow, tier },
                  })
              : undefined
          result.push(
            <Box
              key={`tier-${tier}-col-${colIndex}`}
              onClick={onCellClick}
              onKeyDown={
                onCellClick
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onCellClick()
                      }
                    }
                  : undefined
              }
              role={onCellClick ? 'button' : undefined}
              tabIndex={onCellClick ? 0 : undefined}
              aria-disabled={isIneligibleInAccumulator || undefined}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: journeyId ? undefined : `1px solid ${theme.palette.grey[300]}`,
                borderRadius: theme.customRadius.medium,
                gridRow: `${tierIndex + 1}`,
                gridColumn: `${startCssCol} / span ${gridColumnSpan}`,
                background,
                cursor,
                outline: isAccumulated ? `2px solid ${theme.palette.primary.main}` : undefined,
                outlineOffset: isAccumulated ? '-2px' : undefined,
              }}
            >
              {journeyId &&
                renderContainerDetailsByJourneyId(
                  journeyId,
                  journey.isFiltered,
                  journey.type === OccupancyType.Reserved,
                  resolveOccupancySchemeColor(journey, activeScheme, selectedSchemeSet),
                )}
              {hiddenContainers.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <Box>
                        {t(
                          'tierConflictTooltip',
                          'Multiple containers report this tier (data integrity issue):',
                        )}
                      </Box>
                      <Box component='ul' sx={{ m: 0, pl: 2 }}>
                        {journey?.containerNumber && (
                          <li>
                            {journey.containerNumber} ({t('shown', 'shown')})
                          </li>
                        )}
                        {hiddenContainers.map(c => (
                          <li key={c}>
                            {c} ({t('hidden', 'hidden')})
                          </li>
                        ))}
                      </Box>
                    </Box>
                  }
                >
                  <WarningIcon
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      color: theme.palette.warning.main,
                      fontSize: 18,
                      zIndex: 1,
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              )}
            </Box>,
          )
        }
      }

      return result
    }, [
      columnLength,
      tierLength,
      theme,
      viewStore.focusedYardBlock,
      viewStore.occupanciesForSelectedBayAndRow,
      viewStore.tierConflictsForSelectedBayAndRow,
      renderContainerDetailsByJourneyId,
      getCellBackground,
      cssColumnForDataIndex,
      extraGapColumnsInSpan,
      activeScheme,
      selectedSchemeSet,
      t,
      isAccumulating,
      // accumulatedCount changes on add/remove — needed because observer's tracking
      // doesn't reach observable reads inside this useMemo body.
      housekeepingPlanAccumulatorViewStore.accumulatedCount,
      housekeepingPlanAccumulatorViewStore,
      handleAccumulatorClick,
    ])

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gridTemplateRows: `repeat(${tierLength}, ${CONTAINER_DETAILS_HEIGHT}px)`,
          gap: theme.customSpacing.s,
          justifyContent: columnLength <= 1 && !hasAnyRail ? 'center' : undefined,
        }}
      >
        {railCells}
        {dataCells}
      </Box>
    )
  },
)
